import React, { useRef, useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { drawChart, getShapeBounds } from '../chartUtils';
import { useChartData } from './ChartDataProvider';
import STITCH_TYPES from '../stitchTypes';
import { floodFill } from '../gridUtils';
import { getCursorDataUrl } from './drawingModeIcons';

export default function CanvasEdit({ canvasRef }) {
  const {
    data,
    knitdata,
    rows,
    cols,
    colors,
    settings,
    currentColor,
    selection,
    colorway,
    stitchMode,
    colorMode,
    shapeMode,
    shapeSelectMode,
    currentShape,
    selectedShape,
    shapeStyle,
    shapes,
    selectedStitch,
    isSelect,
    drawingMode,
    actions
  } = useChartData();
  
  const isDrawing = useRef(false);
  const isSelecting = useRef(false);
  const isDrawingShape = useRef(false);
  const isMovingShape = useRef(false);
  const isResizingShape = useRef(false);
  const resizeHandle = useRef(null);
  const shapeStartPos = useRef(null);
  const shapeStartMousePos = useRef(null);
  const [stitchPreview, setStitchPreview] = useState(null);
  const [colorPreview, setColorPreview] = useState(null);
  const [currentShapePreview, setCurrentShapePreview] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [editingText, setEditingText] = useState(null); // For inline text editing
  const lastClickTime = useRef(0);
  const lastClickedShape = useRef(null);

  // Draw chart when relevant state changes, with error handling
  useEffect(() => {
    try {
      if (!canvasRef.current) return;
      if (!Array.isArray(data) || !Array.isArray(colors) || typeof cols !== 'number' || typeof rows !== 'number') {
        // Optionally log or handle invalid props
        return;
      }
      drawChart({ 
        canvasRef, 
        settings, 
        cols, 
        rows, 
        data, 
        colors, 
        selection, 
        colorway: colorway, 
        knitdata, 
        stitchPreview, 
        colorPreview,
        shapes,
        currentShapePreview,
        selectedShape
      });
    } catch (err) {
      // Optionally log error to an error reporting service
      // eslint-disable-next-line no-console
      console.error('Error drawing chart:', err);
    }
  }, [canvasRef, settings, cols, rows, data, colors, selection, colorway, knitdata, stitchPreview, colorPreview, shapes, currentShapePreview, selectedShape]);

  const calculateStitchPreview = (x, y) => {
    if (!stitchMode || !selectedStitch) {
      return null;
    }

    // Calculate margins based on label settings - same logic as chartUtils.js
    const { showLabels = { right: true, left: false, top: false, bottom: true } } = settings;
    const margin = settings.margin || 24; // Use same margin setting as chartUtils
    const leftMargin = showLabels.left ? margin : 0;
    const topMargin = showLabels.top ? margin : 0;

    // x, y are already adjusted for shape margins by getCanvasCoordinates
    // Convert canvas coordinates to visual grid coordinates, accounting for label margins
    const visualCol = Math.floor((x - leftMargin) / settings.cellSize);
    const visualRow = Math.floor((y - topMargin) / (settings.cellSize * (settings.yscale || 1)));
    
    // Since we always use top-down and left-to-right, visual coordinates = data coordinates
    const col = visualCol;
    const row = visualRow;
    
    // Check if coordinates are valid
    if (col < 0 || col >= cols || row < 0 || row >= rows) {
      return null;
    }

    const stitchInfo = STITCH_TYPES[selectedStitch];
    const stitchWidth = stitchInfo?.width || 1;
    
    // Calculate the range of columns this stitch will occupy
    // Since we always use left-to-right: clicked cell is the start, expand rightward
    const startCol = col;
    const endCol = startCol + stitchWidth - 1;
    
    // Check if there's enough space for the stitch in the data array
    if (startCol < 0 || endCol >= cols) {
      return null;
    }

    return {
      row,
      startCol,
      endCol,
      stitchWidth,
      selectedStitch
    };
  };

  const calculateColorPreview = (x, y) => {
    if (!colorMode || currentColor === null) {
      return null;
    }

    // Calculate margins based on label settings - same logic as chartUtils.js
    const { showLabels = { right: true, left: false, top: false, bottom: true } } = settings;
    const margin = settings.margin || 24; // Use same margin setting as chartUtils
    const leftMargin = showLabels.left ? margin : 0;
    const topMargin = showLabels.top ? margin : 0;

    // x, y are already adjusted for shape margins by getCanvasCoordinates
    // Convert canvas coordinates to visual grid coordinates, accounting for label margins
    const visualCol = Math.floor((x - leftMargin) / settings.cellSize);
    const visualRow = Math.floor((y - topMargin) / (settings.cellSize * (settings.yscale || 1)));
    
    // Since we always use top-down and left-to-right, visual coordinates = data coordinates
    const col = visualCol;
    const row = visualRow;
    
    // Check if coordinates are valid
    if (col < 0 || col >= cols || row < 0 || row >= rows) {
      return null;
    }

    return {
      row,
      col,
      currentColor
    };
  };

  const SHAPE_MARGIN = 50; // Should match the shapeMargin in chartUtils.js

  // Helper function to convert mouse event to canvas coordinates accounting for shape margins
  const getCanvasCoordinates = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left - SHAPE_MARGIN,
      y: event.clientY - rect.top - SHAPE_MARGIN
    };
  };

  // Helper function to convert canvas coordinates to grid coordinates
  const canvasToGrid = (x, y, snapToGrid = false) => {
    const { showLabels = { right: true, left: false, top: false, bottom: true } } = settings;
    const margin = settings.margin || 24; // Use same margin setting as chartUtils
    const leftMargin = showLabels.left ? margin : 0;
    const topMargin = showLabels.top ? margin : 0;

    let gridX, gridY;
    
    if (snapToGrid) {
      // Snap to grid for lines and rectangles
      // Use Math.round instead of Math.floor for better snapping to nearest grid line
      const visualCol = Math.round((x - leftMargin) / settings.cellSize);
      const visualRow = Math.round((y - topMargin) / (settings.cellSize * (settings.yscale || 1)));
      
      // Check if coordinates are within the grid area
      const isInGridX = visualCol >= 0 && visualCol <= cols;
      const isInGridY = visualRow >= 0 && visualRow <= rows;
      
      if (isInGridX && isInGridY) {
        // Within grid area - snap to grid lines
        gridX = visualCol * settings.cellSize + leftMargin;
        gridY = visualRow * settings.cellSize * (settings.yscale || 1) + topMargin;
      } else {
        // Outside grid area (in margin) - use raw coordinates (no snapping)
        gridX = x;
        gridY = y;
      }
    } else {
      // Free positioning for text
      gridX = x;
      gridY = y;
    }
    
    return { x: gridX, y: gridY };
  };

  // Helper function to get standardized bounds for any shape type
  const getShapeBoundsLocal = (shape) => {
    if (!shape) return null;
    
    // Create a temporary canvas context for text measurement
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    
    // Use the unified bounds calculation from chartUtils
    return getShapeBounds(tempCtx, shape);
  };

  // Helper function to detect which resize handle was clicked
  const getResizeHandle = (x, y, shape) => {
    if (!shape) return null;
    
    const bounds = getShapeBoundsLocal(shape);
    if (!bounds) return null;
    
    // Use larger detection area for easier clicking (visual handles are 6px in chartUtils)
    const detectionSize = 12; // Larger area for easier clicking
    const handles = [
      { type: 'nw', x: bounds.x, y: bounds.y }, // top-left
      { type: 'ne', x: bounds.x + bounds.width, y: bounds.y }, // top-right
      { type: 'sw', x: bounds.x, y: bounds.y + bounds.height }, // bottom-left
      { type: 'se', x: bounds.x + bounds.width, y: bounds.y + bounds.height }, // bottom-right
    ];
    
    // Add mid-point handles for all resizable shapes
    handles.push(
      { type: 'n', x: bounds.x + bounds.width / 2, y: bounds.y }, // top-middle
      { type: 's', x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height }, // bottom-middle
      { type: 'w', x: bounds.x, y: bounds.y + bounds.height / 2 }, // left-middle
      { type: 'e', x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 } // right-middle
    );
    
    // Check if click is within any handle (using larger detection area)
    for (const handle of handles) {
      const distance = Math.sqrt(
        Math.pow(x - handle.x, 2) + Math.pow(y - handle.y, 2)
      );
      if (distance <= detectionSize) {
        return handle.type;
      }
    }
    
    return null;
  };

  // Helper function to detect which shape was clicked
  const getShapeAtPoint = (x, y) => {
    // Check shapes in reverse order (top to bottom) for hit testing
    for (let i = shapes.length - 1; i >= 0; i--) {
      const shape = shapes[i];
      
      if (isPointInShape(x, y, shape)) {
        return shape;
      }
    }
    
    return null;
  };

  // Helper function to check if a point is within a shape
  const isPointInShape = (x, y, shape) => {
    switch (shape.type) {
      case 'line': {
        // Calculate distance from point to line
        const lineLength = Math.sqrt(
          Math.pow(shape.endX - shape.startX, 2) + 
          Math.pow(shape.endY - shape.startY, 2)
        );
        
        if (lineLength === 0) {
          // Line is actually a point
          const distance = Math.sqrt(
            Math.pow(x - shape.startX, 2) + 
            Math.pow(y - shape.startY, 2)
          );
          return distance <= 5;
        } else {
          // Calculate perpendicular distance from point to line
          const A = x - shape.startX;
          const B = y - shape.startY;
          const C = shape.endX - shape.startX;
          const D = shape.endY - shape.startY;
          
          const dot = A * C + B * D;
          const lenSq = C * C + D * D;
          const param = dot / lenSq;
          
          let nearestX, nearestY;
          
          if (param < 0) {
            nearestX = shape.startX;
            nearestY = shape.startY;
          } else if (param > 1) {
            nearestX = shape.endX;
            nearestY = shape.endY;
          } else {
            nearestX = shape.startX + param * C;
            nearestY = shape.startY + param * D;
          }
          
          const distance = Math.sqrt(
            Math.pow(x - nearestX, 2) + 
            Math.pow(y - nearestY, 2)
          );
          
          return distance <= Math.max(5, (shape.thickness || 1) + 3);
        }
      }
      
      case 'rectangle': {
        const rectX = Math.min(shape.startX, shape.endX);
        const rectY = Math.min(shape.startY, shape.endY);
        const rectWidth = Math.abs(shape.endX - shape.startX);
        const rectHeight = Math.abs(shape.endY - shape.startY);
        
        if (shape.filled) {
          // Check if point is inside filled rectangle
          return x >= rectX && x <= rectX + rectWidth && 
                 y >= rectY && y <= rectY + rectHeight;
        } else {
          // Check if point is near rectangle border
          const thickness = (shape.thickness || 1) + 3;
          return x >= rectX - thickness && x <= rectX + rectWidth + thickness && 
                 y >= rectY - thickness && y <= rectY + rectHeight + thickness &&
                 (x <= rectX + thickness || x >= rectX + rectWidth - thickness ||
                  y <= rectY + thickness || y >= rectY + rectHeight - thickness);
        }
      }
      
      case 'text': {
        // Use unified bounds calculation for consistent hit detection
        const bounds = getShapeBoundsLocal(shape);
        if (!bounds) return false;
        
        return x >= bounds.x && x <= bounds.x + bounds.width &&
               y >= bounds.y && y <= bounds.y + bounds.height;
      }
      
      case 'icon': {
        // Icons are square shapes positioned at startX, startY with size property
        const size = shape.size || 16;
        return x >= shape.startX && x <= shape.startX + size &&
               y >= shape.startY && y <= shape.startY + size;
      }
      
      default:
        return false;
    }
  };

  // Helper function to create a shape object
  const createShape = (type, startPos, endPos, style, text = '') => {
    const shape = {
      id: Date.now() + Math.random(), // Simple unique ID
      type,
      ...style,
      startX: startPos.x,
      startY: startPos.y
    };

    if (type === 'line' || type === 'rectangle') {
      shape.endX = endPos.x;
      shape.endY = endPos.y;
    }

    if (type === 'text') {
      shape.text = text || 'Text';
      // Set default dimensions for text shapes
      shape.width = Math.max(100, (text || 'Text').length * (style.fontSize || 12) * 0.6);
      shape.height = (style.fontSize || 12) * 1.2;
    }

    if (type === 'icon') {
      // Icons don't need endX/endY, they're positioned by startX/startY and sized by size property
      // Icon-specific properties like iconType, size, rotation, filled are already included via style spread
    }

    return shape;
  };

  const updateCell = (x, y) => {
    // Calculate margins based on label settings
    const { showLabels = { right: true, left: false, top: false, bottom: true } } = settings;
    const margin = settings.margin || 24; // Use same margin setting as chartUtils
    const leftMargin = showLabels.left ? margin : 0;
    const topMargin = showLabels.top ? margin : 0;

    // Convert canvas coordinates to visual grid coordinates, accounting for margins
    const col = Math.floor((x - leftMargin) / settings.cellSize);
    const row = Math.floor((y - topMargin) / (settings.cellSize * (settings.yscale || 1)));
    
    if (col >= 0 && col < cols && row >= 0 && row < rows) {
      if (stitchMode) {
        // Handle stitch placement - use the same logic as calculateStitchPreview
        const stitchInfo = STITCH_TYPES[selectedStitch];
        const stitchWidth = stitchInfo?.width || 1;
        
        let startCol, endCol;
        
        // Since we always use left-to-right: clicked cell is the start, expand rightward
        startCol = col;
        endCol = startCol + stitchWidth - 1;
        
        // Check if there's enough space for the stitch in the data array
        if (startCol >= 0 && endCol < cols) {
          actions.placeStitch(row, startCol);
        }
      } else if (colorMode) {
        // Handle color placement only when color mode is enabled
        const newColor = currentColor === 'no-stitch' ? null : currentColor;
        
        if (drawingMode === 'fill') {
          // Use flood fill for adjacent cells
          const originalColor = data[row][col];
          if (originalColor !== newColor) {
            const newData = floodFill(data, row, col, newColor);
            actions.updateData(newData);
          }
        } else {
          // Single cell mode (default)
          actions.updateData(data.map((r, rIndex) => 
            r.map((c, cIndex) => 
              rIndex === row && cIndex === col ? newColor : c
            )
          ));
        }
      }
    }
  };

  function handleStart(event) {
    if (shapeMode && !shapeSelectMode && currentShape) {
      // Drawing a new shape
      handleStartShape(event);
    } else if (shapeMode && shapeSelectMode) {
      // Shape selection/movement mode
      handleStartShapeSelect(event);
    } else if (isSelect && !stitchMode) {
      handleStartSelect(event);
    } else {
      handleStartDraw(event);
    }
  }

  function handleMove(event) {
    if (shapeMode && !shapeSelectMode && currentShape) {
      handleMoveShape(event);
    } else if (shapeMode && shapeSelectMode) {
      handleMoveShapeSelect(event);
    } else if (isSelect && !stitchMode) {
      handleMoveSelect(event);
    } else {
      handleMoveDraw(event);
    }
  }

  function handleEnd() {
    if (shapeMode && !shapeSelectMode && currentShape) {
      handleEndShape();
    } else if (shapeMode && shapeSelectMode) {
      handleEndShapeSelect();
    } else if (isSelect && !stitchMode) {
      handleEndSelect();
    } else {
      handleEndDraw();
    }
  }

  const handleStartDraw = (event) => {
    isDrawing.current = true;
    // Clear previews when starting to draw
    if (stitchMode) {
      setStitchPreview(null);
    }
    if (colorMode) {
      setColorPreview(null);
    }
    const { x, y } = getCanvasCoordinates(event);
    updateCell(x, y);
  };

  const handleMoveDraw = (event) => {
    if (!isDrawing.current) return;
    
    const { x, y } = getCanvasCoordinates(event);
    updateCell(x, y);
  };

  const handleMouseMove = (event) => {
    // Always track mouse position for cursor styling
    const { x, y } = getCanvasCoordinates(event);
    setMousePosition({ x, y });

    if (isDrawing.current || isSelecting.current || isDrawingShape.current || isMovingShape.current || isResizingShape.current) {
      // Handle continuous drawing, selection, shape drawing, moving, or resizing
      handleMove(event);
    } else if (shapeMode && currentShape) {
      // Clear other previews in shape mode
      setStitchPreview(null);
      setColorPreview(null);
    } else if (stitchMode) {
      // Update stitch preview on hover in stitch mode when not drawing
      const preview = calculateStitchPreview(x, y);
      setStitchPreview(preview);
      // Clear color preview when in stitch mode
      setColorPreview(null);
    } else if (colorMode) {
      // Update color preview on hover in color mode when not drawing
      const preview = calculateColorPreview(x, y);
      setColorPreview(preview);
      // Clear stitch preview when in color mode
      setStitchPreview(null);
    } else {
      // Clear all previews when not in any mode
      setStitchPreview(null);
      setColorPreview(null);
      setCurrentShapePreview(null);
    }
  };

  const handleMouseLeave = () => {
    // Clear all previews when mouse leaves canvas
    if (stitchMode) {
      setStitchPreview(null);
    }
    if (colorMode) {
      setColorPreview(null);
    }
    if (shapeMode) {
      setCurrentShapePreview(null);
    }
    handleEnd();
  };

  const handleEndDraw = () => {
    isDrawing.current = false;
    // Note: We don't immediately restore the stitch preview here because
    // the next mouse move event will handle it naturally when hovering
  };

  // Shape selection handlers
  const handleStartShapeSelect = (event) => {
    event.preventDefault(); // Prevent default drag behavior
    
    const { x, y } = getCanvasCoordinates(event);

    // First priority: Check for resize handles on selected shape
    if (selectedShape) {
      const handle = getResizeHandle(x, y, selectedShape);
      
      if (handle) {
        // Start resizing
        isResizingShape.current = true;
        resizeHandle.current = handle;
        shapeStartMousePos.current = { x, y };
        
        // Store the original shape bounds
        shapeStartPos.current = {
          startX: selectedShape.startX,
          startY: selectedShape.startY,
          endX: selectedShape.endX,
          endY: selectedShape.endY
        };
        return; // Exit early for resize
      }
    }

    // Second priority: Check which shape was clicked
    const clickedShape = getShapeAtPoint(x, y);
    const currentTime = Date.now();
    const isDoubleClick = 
      clickedShape && 
      lastClickedShape.current && 
      clickedShape.id === lastClickedShape.current.id && 
      currentTime - lastClickTime.current < 300; // 300ms double-click threshold for same shape
    
    // Also consider it a double-click if clicking on any text shape within the double-click timeframe
    const isTextDoubleClick = 
      clickedShape && 
      clickedShape.type === 'text' &&
      currentTime - lastClickTime.current < 300; // 300ms double-click threshold for any text shape
    
    if (clickedShape) {
      if ((isDoubleClick || isTextDoubleClick) && clickedShape.type === 'text') {
        // If we're already editing text on another shape, save that edit first
        if (editingText && editingText.id !== clickedShape.id) {
          // Get the current value from the textarea element
          const textElement = document.querySelector('textarea[style*="position: absolute"]');
          if (textElement) {
            actions.updateShape(editingText.id, { text: textElement.value });
          }
        }
        
        // Start inline text editing on double-click
        setEditingText({
          id: clickedShape.id,
          x: clickedShape.startX,
          y: clickedShape.startY,
          text: clickedShape.text || 'Text'
        });
        return;
      }
      
      if (selectedShape && selectedShape.id === clickedShape.id && !isDoubleClick && !isTextDoubleClick) {
        // Start moving the already selected shape on single click
        isMovingShape.current = true;
        shapeStartMousePos.current = { x, y };
        
        // Store the original shape position
        shapeStartPos.current = {
          startX: selectedShape.startX,
          startY: selectedShape.startY,
          endX: selectedShape.endX,
          endY: selectedShape.endY
        };
      } else if (!isDoubleClick && !isTextDoubleClick) {
        // If we're editing text and clicking on a non-text shape, save the current edit first
        if (editingText) {
          const textElement = document.querySelector('textarea[style*="position: absolute"]');
          if (textElement) {
            actions.updateShape(editingText.id, { text: textElement.value });
          }
          setEditingText(null);
        }
        
        // Select a different shape on single click
        actions.setSelectedShape(clickedShape);
        isMovingShape.current = false;
        isResizingShape.current = false;
      }
      
      // Update double-click tracking
      lastClickTime.current = currentTime;
      lastClickedShape.current = clickedShape;
    } else {
      // If we're editing text and clicking on empty space, save the current edit
      if (editingText) {
        const textElement = document.querySelector('textarea[style*="position: absolute"]');
        if (textElement) {
          actions.updateShape(editingText.id, { text: textElement.value });
        }
        setEditingText(null);
      }
      
      // Clear selection if clicking empty space
      actions.setSelectedShape(null);
      isMovingShape.current = false;
      isResizingShape.current = false;
      lastClickedShape.current = null;
    }
  };

  const handleMoveShapeSelect = (event) => {
    if ((!isMovingShape.current && !isResizingShape.current) || !selectedShape || !shapeStartMousePos.current || !shapeStartPos.current) {
      return;
    }

    event.preventDefault(); // Prevent default drag behavior

    const { x, y } = getCanvasCoordinates(event);

    if (isResizingShape.current) {
      // Handle resizing
      handleShapeResize(x, y);
    } else if (isMovingShape.current) {
      // Handle moving
      handleShapeMove(x, y);
    }
  };

  const handleShapeMove = (x, y) => {
    // Calculate the movement delta
    const deltaX = x - shapeStartMousePos.current.x;
    const deltaY = y - shapeStartMousePos.current.y;

    // Only proceed if there's meaningful movement (more than 2 pixels)
    if (Math.abs(deltaX) < 2 && Math.abs(deltaY) < 2) {
      return;
    }

    // Calculate new position
    const newStartX = shapeStartPos.current.startX + deltaX;
    const newStartY = shapeStartPos.current.startY + deltaY;
    
    // Use freeform positioning (no grid snapping)
    const newStart = { x: newStartX, y: newStartY };

    // Update the shape position
    const updates = {
      startX: newStart.x,
      startY: newStart.y
    };

    // For lines and rectangles, also update the end position maintaining relative offset
    if (selectedShape.type === 'line' || selectedShape.type === 'rectangle') {
      const originalWidth = shapeStartPos.current.endX - shapeStartPos.current.startX;
      const originalHeight = shapeStartPos.current.endY - shapeStartPos.current.startY;
      
      updates.endX = newStart.x + originalWidth;
      updates.endY = newStart.y + originalHeight;
    }

    // Update the shape in the store
    actions.updateShape(selectedShape.id, updates);
  };

  const handleShapeResize = (x, y) => {
    const handle = resizeHandle.current;
    if (!handle || !selectedShape) return;

    // Use raw mouse position for freeform resize (no grid snapping)
    const mousePos = { x, y };
    
    // Use unified resize logic
    const updates = getShapeResizeUpdates(selectedShape, handle, mousePos);
    
    if (Object.keys(updates).length > 0) {
      actions.updateShape(selectedShape.id, updates);
    }
  };

  // Unified function to calculate resize updates for any shape type
  const getShapeResizeUpdates = (shape, handle, snappedMouse) => {
    let updates = {};
    
    switch (shape.type) {
      case 'line':
        updates = getLineResizeUpdates(shape, handle, snappedMouse);
        break;
      case 'rectangle':
        updates = getRectangleResizeUpdates(shape, handle, snappedMouse);
        break;
      case 'text':
        updates = getTextResizeUpdates(shape, handle, snappedMouse);
        break;
      case 'icon':
        updates = getIconResizeUpdates(shape, handle, snappedMouse);
        break;
      default:
        return {};
    }
    
    return updates;
  };

  // Shape-specific resize update calculators
  const getLineResizeUpdates = (shape, handle, snappedMouse) => {
    const updates = {};
    
    switch (handle) {
      case 'nw':
        updates.startX = snappedMouse.x;
        updates.startY = snappedMouse.y;
        break;
      case 'ne':
        updates.endX = snappedMouse.x;
        updates.startY = snappedMouse.y;
        break;
      case 'sw':
        updates.startX = snappedMouse.x;
        updates.endY = snappedMouse.y;
        break;
      case 'se':
        updates.endX = snappedMouse.x;
        updates.endY = snappedMouse.y;
        break;
      case 'n':
        updates.startY = snappedMouse.y;
        break;
      case 's':
        updates.endY = snappedMouse.y;
        break;
      case 'w':
        updates.startX = snappedMouse.x;
        break;
      case 'e':
        updates.endX = snappedMouse.x;
        break;
    }
    
    // Ensure all coordinates are preserved
    return {
      startX: updates.startX !== undefined ? updates.startX : shape.startX,
      startY: updates.startY !== undefined ? updates.startY : shape.startY,
      endX: updates.endX !== undefined ? updates.endX : shape.endX,
      endY: updates.endY !== undefined ? updates.endY : shape.endY
    };
  };

  const getRectangleResizeUpdates = (shape, handle, snappedMouse) => {
    const updates = {};
    
    switch (handle) {
      case 'nw':
        updates.startX = snappedMouse.x;
        updates.startY = snappedMouse.y;
        break;
      case 'ne':
        updates.endX = snappedMouse.x;
        updates.startY = snappedMouse.y;
        break;
      case 'sw':
        updates.startX = snappedMouse.x;
        updates.endY = snappedMouse.y;
        break;
      case 'se':
        updates.endX = snappedMouse.x;
        updates.endY = snappedMouse.y;
        break;
      case 'n':
        updates.startY = snappedMouse.y;
        break;
      case 's':
        updates.endY = snappedMouse.y;
        break;
      case 'w':
        updates.startX = snappedMouse.x;
        break;
      case 'e':
        updates.endX = snappedMouse.x;
        break;
    }
    
    // Ensure all coordinates are preserved
    return {
      startX: updates.startX !== undefined ? updates.startX : shape.startX,
      startY: updates.startY !== undefined ? updates.startY : shape.startY,
      endX: updates.endX !== undefined ? updates.endX : shape.endX,
      endY: updates.endY !== undefined ? updates.endY : shape.endY
    };
  };

  const getTextResizeUpdates = (shape, handle, snappedMouse) => {
    const updates = {};
    const currentWidth = shape.width || 100;
    const currentHeight = shape.height || (shape.fontSize || 12) * 1.2;
    
    // Calculate deltas from original position
    const deltaX = snappedMouse.x - shape.startX;
    const deltaY = snappedMouse.y - shape.startY;
    
    switch (handle) {
      case 'nw': {
        // Move top-left, adjust both dimensions
        const newWidthNW = currentWidth - deltaX;
        const newHeightNW = currentHeight - deltaY;
        if (newWidthNW >= 50) {
          updates.startX = snappedMouse.x;
          updates.width = newWidthNW;
        }
        if (newHeightNW >= 20) {
          updates.startY = snappedMouse.y;
          updates.height = newHeightNW;
        }
        break;
      }
      case 'ne': {
        // Move top-right, adjust width and height
        updates.width = Math.max(50, deltaX);
        const newHeightNE = currentHeight - deltaY;
        if (newHeightNE >= 20) {
          updates.startY = snappedMouse.y;
          updates.height = newHeightNE;
        }
        break;
      }
      case 'sw': {
        // Move bottom-left, adjust width and height
        const newWidthSW = currentWidth - deltaX;
        if (newWidthSW >= 50) {
          updates.startX = snappedMouse.x;
          updates.width = newWidthSW;
        }
        updates.height = Math.max(20, deltaY);
        break;
      }
      case 'se':
        // Move bottom-right, adjust both dimensions
        updates.width = Math.max(50, deltaX);
        updates.height = Math.max(20, deltaY);
        break;
      case 'n': {
        // Move top edge, adjust height
        const newHeightN = currentHeight - deltaY;
        if (newHeightN >= 20) {
          updates.startY = snappedMouse.y;
          updates.height = newHeightN;
        }
        break;
      }
      case 's':
        // Move bottom edge, adjust height
        updates.height = Math.max(20, deltaY);
        break;
      case 'w': {
        // Move left edge, adjust width
        const newWidthW = currentWidth - deltaX;
        if (newWidthW >= 50) {
          updates.startX = snappedMouse.x;
          updates.width = newWidthW;
        }
        break;
      }
      case 'e':
        // Move right edge, adjust width
        updates.width = Math.max(50, deltaX);
        break;
    }
    
    return updates;
  };

  const getIconResizeUpdates = (shape, handle, snappedMouse) => {
    const updates = {};
    const currentSize = shape.size || 16;
    
    // Calculate distance from shape center to mouse position
    const centerX = shape.startX + currentSize / 2;
    const centerY = shape.startY + currentSize / 2;
    const distanceFromCenter = Math.sqrt(
      Math.pow(snappedMouse.x - centerX, 2) + 
      Math.pow(snappedMouse.y - centerY, 2)
    );
    
    // Calculate new size based on distance from center (radius becomes size/2)
    const newSize = Math.max(12, Math.min(60, distanceFromCenter * 2));
    
    // For icons, we only need to update the size - they stay centered
    updates.size = Math.round(newSize);
    
    return updates;
  };

  const handleEndShapeSelect = () => {
    isMovingShape.current = false;
    isResizingShape.current = false;
    resizeHandle.current = null;
    shapeStartMousePos.current = null;
    shapeStartPos.current = null;
  };

  // Shape drawing handlers
  const handleStartShape = (event) => {
    const { x, y } = getCanvasCoordinates(event);

    if (currentShape === 'text') {
      // For text, place immediately
      const pos = canvasToGrid(x, y, false); // Don't snap text to grid
      const text = prompt('Enter text:');
      if (text) {
        const shape = createShape('text', pos, pos, shapeStyle, text);
        actions.addShape(shape);
      }
    } else if (currentShape === 'icon') {
      // For icons, place immediately
      const pos = canvasToGrid(x, y, false); // Don't snap icons to grid
      const shape = createShape('icon', pos, pos, shapeStyle);
      actions.addShape(shape);
    } else {
      // For line and rectangle, start drawing mode
      isDrawingShape.current = true;
      const pos = canvasToGrid(x, y, false); // No grid snapping - freeform like text
      shapeStartPos.current = pos;
      setCurrentShapePreview({ 
        type: currentShape, 
        startX: pos.x, 
        startY: pos.y, 
        endX: pos.x, 
        endY: pos.y,
        ...shapeStyle
      });
    }
  };

  const handleMoveShape = (event) => {
    if (!isDrawingShape.current || !shapeStartPos.current) return;

    const { x, y } = getCanvasCoordinates(event);
    const pos = canvasToGrid(x, y, false); // No grid snapping - freeform positioning

    setCurrentShapePreview({
      type: currentShape,
      startX: shapeStartPos.current.x,
      startY: shapeStartPos.current.y,
      endX: pos.x,
      endY: pos.y,
      ...shapeStyle
    });
  };

  const handleEndShape = () => {
    if (isDrawingShape.current && shapeStartPos.current && currentShapePreview) {
      // Create the final shape
      const shape = createShape(
        currentShape,
        { x: currentShapePreview.startX, y: currentShapePreview.startY },
        { x: currentShapePreview.endX, y: currentShapePreview.endY },
        shapeStyle
      );
      actions.addShape(shape);
    }

    // Reset shape drawing state
    isDrawingShape.current = false;
    shapeStartPos.current = null;
    setCurrentShapePreview(null);
  };

  const handleStartSelect = (event) => {
    const { x, y } = getCanvasCoordinates(event);

    // Calculate margins based on label settings
    const { showLabels = { right: true, left: false, top: false, bottom: true } } = settings;
    const margin = settings.margin || 24; // Use same margin setting as chartUtils
    const leftMargin = showLabels.left ? margin : 0;
    const topMargin = showLabels.top ? margin : 0;

    // Convert canvas coordinates to visual grid coordinates, accounting for margins
    const startCol = Math.floor((x - leftMargin) / settings.cellSize);
    const startRow = Math.floor((y - topMargin) / (settings.cellSize * (settings.yscale || 1)));
    
    // Clamp to valid bounds
    const clampedStartCol = Math.max(0, Math.min(cols - 1, startCol));
    const clampedStartRow = Math.max(0, Math.min(rows - 1, startRow));

    actions.setSelection({ startRow: clampedStartRow, startCol: clampedStartCol, endRow: clampedStartRow, endCol: clampedStartCol });
    isSelecting.current = true;
  };

  const handleMoveSelect = (event) => {
    if (!isSelecting.current) return;

    const { x, y } = getCanvasCoordinates(event);

    // Calculate margins based on label settings - same logic as chartUtils.js
    const { showLabels = { right: true, left: false, top: false, bottom: true } } = settings;
    const margin = settings.margin || 24; // Use same margin setting as chartUtils
    const leftMargin = showLabels.left ? margin : 0;
    const topMargin = showLabels.top ? margin : 0;

    // Convert canvas coordinates to visual grid coordinates, accounting for margins
    const endCol = Math.floor((x - leftMargin) / settings.cellSize);
    const endRow = Math.floor((y - topMargin) / (settings.cellSize * (settings.yscale || 1)));
    
    // Clamp to valid bounds
    const clampedEndCol = Math.max(0, Math.min(cols - 1, endCol));
    const clampedEndRow = Math.max(0, Math.min(rows - 1, endRow));

    actions.setSelection({
      ...selection,
      endRow: clampedEndRow,
      endCol: clampedEndCol
    });
  };

  const handleEndSelect = () => {
    isSelecting.current = false;
  };

  // Handle text editing
  const handleTextEdit = (newText) => {
    if (editingText) {
      actions.updateShape(editingText.id, { text: newText });
    }
    setEditingText(null);
  };

  const handleTextEditCancel = () => {
    setEditingText(null);
  };

  // Determine cursor style based on mode
  const getCursorStyle = () => {
    if (shapeMode && shapeSelectMode) {
      // Check if mouse is over a selected shape
      if (selectedShape) {
        const resizeHandleType = getResizeHandle(mousePosition.x, mousePosition.y, selectedShape);
        if (resizeHandleType) {
          // Show appropriate resize cursor based on handle type
          switch (resizeHandleType) {
            case 'nw':
            case 'se':
              return 'nw-resize';
            case 'ne':
            case 'sw':
              return 'ne-resize';
            case 'n':
            case 's':
              return 'ns-resize';
            case 'w':
            case 'e':
              return 'ew-resize';
            default:
              return 'default';
          }
        }
        
        const shapeAtMouse = getShapeAtPoint(mousePosition.x, mousePosition.y);
        if (shapeAtMouse && shapeAtMouse.id === selectedShape.id) {
          return 'move'; // Show move cursor when hovering over selected shape
        }
      }
      return 'pointer'; // Better cursor for shape selection
    } else if (shapeMode && currentShape) {
      return 'crosshair'; // Drawing mode
    } else if (isSelect) {
      return 'crosshair';
    } else if (colorMode) {
      if (drawingMode === 'fill') {
        return `url("${getCursorDataUrl('diagonal-diamond')}") 8 8, auto`;
      } else {
        return `url("${getCursorDataUrl('hollow-square')}") 8 8, auto`;
      }
    } else if (stitchMode) {
      return 'crosshair';
    } else {
      return 'default';
    }
  };

  return (
    <Box sx={{ mt: 2, position: 'relative' }}>
      <Box sx={{ overflowX: 'auto' }}>
        <canvas
          ref={canvasRef}
          style={{ 
            touchAction: 'none',
            cursor: getCursorStyle()
          }}
          onMouseDown={handleStart}
          onMouseMove={handleMouseMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />
        
        {/* Inline Text Editor */}
        {editingText && (
          <Box
            style={{
              position: 'absolute',
              left: `${editingText.x}px`,
              top: `${editingText.y - 12}px`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: '4px',
              zIndex: 1000
            }}
          >
            <textarea
              defaultValue={editingText.text}
              placeholder="Enter text... (Use Enter for new lines)"
              autoFocus
              rows={Math.max(1, (editingText.text || 'Text').split('\n').length)}
              style={{
                fontSize: `${(selectedShape?.fontSize || shapeStyle.fontSize || 12)}px`,
                fontFamily: selectedShape?.fontFamily || shapeStyle.fontFamily || 'Arial',
                fontWeight: (selectedShape?.bold || shapeStyle.bold) ? 'bold' : 'normal',
                color: selectedShape?.color || shapeStyle.color || '#000000',
                background: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid #0066ff',
                borderRadius: '2px',
                padding: '2px 4px',
                minWidth: '100px',
                minHeight: '20px',
                resize: 'both',
                overflow: 'hidden'
              }}
              onBlur={(e) => {
                // Save text when losing focus (clicking outside)
                handleTextEdit(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  // Ctrl+Enter or Cmd+Enter to save and exit
                  handleTextEdit(e.target.value);
                  e.preventDefault();
                } else if (e.key === 'Escape') {
                  handleTextEditCancel();
                  e.preventDefault();
                }
                // Allow normal Enter for new lines
              }}
              onInput={(e) => {
                // Auto-resize textarea based on content
                const textarea = e.target;
                const lines = textarea.value.split('\n').length;
                textarea.rows = Math.max(1, lines);
              }}
              onClick={(e) => e.stopPropagation()} // Prevent canvas click events
            />
            
            {/* Save and Cancel Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <IconButton
                size="small"
                color="success"
                onMouseDown={(e) => {
                  // Prevent blur event when clicking the save button
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const textarea = e.target.closest('div').previousElementSibling;
                  handleTextEdit(textarea.value);
                }}
                sx={{ 
                  bgcolor: 'success.main',
                  color: 'success.contrastText',
                  '&:hover': {
                    bgcolor: 'success.dark'
                  }
                }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              
              <IconButton
                size="small"
                color="error"
                onMouseDown={(e) => {
                  // Prevent blur event when clicking the cancel button
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleTextEditCancel();
                }}
                sx={{ 
                  bgcolor: 'error.main',
                  color: 'error.contrastText',
                  '&:hover': {
                    bgcolor: 'error.dark'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
